import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5f (builder B) — the Cost Accounting trio (ca_summary / ca_activities
 * / ca_materials), ported 1:1 from the legacy tabs. The menu dispatches the
 * ca_* values, whose RequireJS aliases map to these dirs (report/mvc/
 * app.js:77-79: 'view.ca_summary' → tabs/cost_accounting_summary/view/
 * ca_summary etc.). The OTHER aliases at app.js:214/:216 are dead references:
 * 'view.cost_accounting_summary' points at tabs/cost_accounting_summary/view/
 * cost_accounting_summary.js — a file that does not exist on disk (the dir
 * ships only ca_summary.js) — and 'view.cost_accounting_detailed' at a
 * tabs/cost_accounting_detailed/ dir that does not exist either; nothing
 * requires either module id, so there is nothing to port behind them.
 *
 * The three views are structurally identical (ca_summary.js 223 lines,
 * ca_activities.js/ca_materials.js 220 lines). LIVE behavior:
 *
 *  - initialize render()s the static shell SYNCHRONOUSLY at construction
 *    (ca_summary.js:32-39, ca_activities.js:30-36, ca_materials.js:30-36),
 *    clearing the .report-tabs-preloader (render :43/:40/:40) — the
 *    filter_data pattern → IMMEDIATE_RENDER_TABS entries; `rendered` is
 *    ignored here.
 *  - LIVE generate: POST 'ca_costing/project' (ca_summary.js:85-94 — the SAME
 *    URL for both the ahu and project branches) / 'ca_activities/project'
 *    (ca_activities.js:136-145) / 'ca_materials/project'
 *    (ca_materials.js:136-145) with the whole state + mfg_id 1; `delete
 *    data.ahu_id` when unset/'0' mutates the shared dispatch state (the kit's
 *    caPayload reproduces the mutation — see generateReport.ts). The $.ajax
 *    SUCCESS only re-render()s the SAME static shell (ca_summary.js:100-102)
 *    — idempotent; delivery happens via the notifications poll.
 *
 * Dead legacy machinery deliberately NOT ported (nothing ever reaches it):
 *  - the report.ahu.tmpl / report.project.tmpl result tables: renderReport
 *    (ca_summary.js:47-68, ca_activities.js:44-66, ca_materials.js:44-66) is
 *    reachable only from (a) the fully commented-out generate socket block
 *    (ca_summary.js:110-123, ca_activities.js:161-173,
 *    ca_materials.js:161-173) and (b) openGeneratedReport
 *    (ca_summary.js:69-77), which can never render: generateReport assigns
 *    the dispatch-state OBJECT to this.data (`self.data = data`,
 *    ca_summary.js:81), so the string-keyed cache lookup `this.data[state]`
 *    (:71) is always undefined → generated stays false → the empty board
 *    re-activates (the page default; NOT in REOPENABLE_TABS). Even a
 *    hypothetical hit would call renderReport(state) with ONE argument (:72)
 *    — type=state, state=undefined → `self.data[undefined].data` throws.
 *    The formatter loop (ca_summary.js:56-63) and setTimeStamp (:64) run only
 *    there, so the header time-stamp NEVER changes from the template literal.
 *  - export_csv (ca_summary.js:127-179, ca_activities.js:67-119,
 *    ca_materials.js:76-128): the only trigger is the header's
 *    'click #export_csv' chain and main_header.tmpl renders no #export_csv
 *    button (only #generate_report) — unreachable; it would also throw at
 *    once on `self.data[self.current_state].data.generate_id` (a cache no
 *    live code writes). Server-side the CSV routes are gone too:
 *    report/urls.py comments out ca_materials/ca_activities export/csv
 *    (urls.py:101-102,:106-107) and no ca_summary/export/csv route exists at
 *    all.
 *  - export_pdf: the listeners (ca_summary.js:36, ca_activities.js:34,
 *    ca_materials.js:34) bind `this.export_pdf` — a method that does not
 *    exist on any of the three views — dead even as a binding.
 *  - ca_summary's initialize `self.mfg = Mfg` (:37): no `self` variable is in
 *    scope there, so it writes window.self.mfg — a global leak with no live
 *    effect; the fn['current.mfg'] dependency (:11) resolves the shared mfg
 *    promise (no tab-specific request). The console.logs
 *    (ca_summary.js:50,:82 et al.) are debug noise, not ported.
 *
 * Template quirks (tmpl/cost_accounting_summary.tmpl /
 * cost_accounting_activities.tmpl / cost_accounting_materials.tmpl — all 1:1):
 *  - ca_summary: report-name has ONE leading space; time-stamp is
 *    "Generated on: " with an EMPTY tail (setTimeStamp only ran from dead
 *    code, so it stays that way).
 *  - ca_activities: report-name has TWO leading spaces; time-stamp keeps the
 *    hardcoded 2017 placeholder.
 *  - ca_materials: group-name is "Materials" [sic — not the menu's
 *    Accounting group]; two leading spaces; 2017 placeholder.
 *  - all three: group-name and report-name spans are ADJACENT (no whitespace
 *    node between them); the content shell is
 *    `col-xs-12 content-container report-content` with inline height:100%
 *    (NOT the report-content col-xs-12 shape of the 5c shells), content
 *    full-height → content-item full-height overflow:auto → the
 *    generation-in-progress preloader, and NO structure div.
 */

interface CaShellProps extends GeneratedTabPaneProps {
  /** Exact .group-name text ("Accounting" / "Materials" [sic]). */
  group: string;
  /** Exact leading whitespace inside .report-name (one or two spaces). */
  reportNameSpaces: string;
  /** Exact .time-stamp literal — never updated by live code. */
  timeStampText: string;
}

/** The shared shell of the three CA templates (identical except the three
 *  quirk props). Renders as soon as the pane mounts (initialize → render) —
 *  `rendered` is ignored, like FilterDataTab; the POST-success re-render is a
 *  no-op re-render of the same markup (ca_summary.js:100-102). */
const CaTabShell = ({ report, faded, group, reportNameSpaces, timeStampText }: CaShellProps) => (
  // Wrapper = the static pane div (main.tmpl:104-106, class="col-xs-12") plus
  // the 'active' openReport adds at generate time (action.js:84).
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    <div className="container-header clearfix">
      <h3 className="left">
        {/* sectionName = the .current_report menu button's text, i.e. the
            report display name (ca_summary.js:42-44). */}
        <span className="group-name" tooltip={`: ${report.name}`}>
          {group}
        </span>
        <span className="report-name">
          {reportNameSpaces}
          {report.name}
        </span>{' '}
        <span className="time-stamp">{timeStampText}</span>
      </h3>
    </div>
    <div className="col-xs-12 content-container report-content" style={{ height: '100%' }}>
      <div className="content full-height">
        <div className="content-item full-height" style={{ overflow: 'auto' }}>
          <div
            className={`preloader-container generation-in-progress${
              faded ? ' opacity-0' : ''
            }`}
          >
            <div className="preloader active">Report generation in progress...</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** cost_accounting_summary.tmpl:1-15. */
export const CaSummaryTab = (props: GeneratedTabPaneProps) => (
  <CaTabShell
    {...props}
    group="Accounting"
    reportNameSpaces={' '}
    timeStampText="Generated on: "
  />
);

/** cost_accounting_activities.tmpl:1-14. */
export const CaActivitiesTab = (props: GeneratedTabPaneProps) => (
  <CaTabShell
    {...props}
    group="Accounting"
    reportNameSpaces={'  '}
    timeStampText="Generated on: 2017.09.13 19:10:46"
  />
);

/** cost_accounting_materials.tmpl:1-14 — group "Materials" [sic]. */
export const CaMaterialsTab = (props: GeneratedTabPaneProps) => (
  <CaTabShell
    {...props}
    group="Materials"
    reportNameSpaces={'  '}
    timeStampText="Generated on: 2017.09.13 19:10:46"
  />
);
